import { Page, PageEntry, PageStatus, PagesState } from '../../state';
import { PageAction, PageActionType } from '../../actions';
import produce, { Draft } from 'immer';
import { cloneDeep } from 'lodash';
import { serializeErrors } from '../serialize-errors';

const initialState: PagesState = {};

const getPage = <A extends { pageKey: string }>(
  state: Draft<PagesState>,
  action: A,
): Draft<PageEntry> => {
  const draft = state[action.pageKey];
  if (!draft) {
    throw new Error('Draft not found.');
  }
  return draft;
};

const setPageStatus = <A extends { errors?: Error[]; pageKey: string }>(
  state: Draft<PagesState>,
  action: A,
  status: PageStatus,
): void => {
  const page = getPage(state, action);
  page.status = status;
  if (action.errors) {
    page.errors = serializeErrors(action.errors);
  } else {
    page.errors = null;
  }
};

const emptyPage: Page = Object.freeze({
  categoryIds: [],
  content: null,
  contentType: null,
  description: '',
  discourageIndexing: false,
  id: '',
  path: null,
  pinterestDescription: '',
  posterImages: [],
  publishAt: null,
  tagIds: [],
  tags: '',
  title: '',
  unpublishAt: null,
});

const emptyPageEntry: PageEntry = Object.freeze({
  errors: null,
  page: emptyPage,
  status: null,
});

export const pagesReducer = (
  state: PagesState = initialState,
  action: PageAction,
): PagesState =>
  produce(state, (draft): void => {
    switch (action.type) {
      case PageActionType.CreatePageError:
        return setPageStatus(draft, action, PageStatus.CreateError);

      case PageActionType.CreatePageRequest:
        draft[action.pageKey] = cloneDeep(emptyPageEntry);
        return;

      case PageActionType.CreatePageSuccess: {
        draft[action.pageKey].page = {
          ...emptyPage,
          ...action.page,
        };
        return setPageStatus(draft, action, PageStatus.CreateSuccess);
      }

      case PageActionType.LoadPageError:
        return setPageStatus(draft, action, PageStatus.LoadError);

      case PageActionType.LoadPageRequest:
        draft[action.pageKey] = cloneDeep(emptyPageEntry);
        return setPageStatus(draft, action, PageStatus.LoadRequested);

      case PageActionType.LoadPageSuccess:
        draft[action.pageKey].page = {
          ...emptyPage,
          ...action.page,
        };
        return setPageStatus(draft, action, PageStatus.LoadSuccess);

      case PageActionType.SetPageCategoryIds:
        getPage(draft, action).page.categoryIds = action.categoryIds;
        return;

      case PageActionType.SetPageContent:
        getPage(draft, action).page.content = action.content;
        return;

      case PageActionType.SetPageContentType:
        getPage(draft, action).page.contentType = action.contentType;
        return;

      case PageActionType.SetPageDescription:
        getPage(draft, action).page.description = action.description;
        return;

      case PageActionType.SetPageDiscourageIndexing:
        getPage(draft, action).page.discourageIndexing =
          action.discourageIndexing;
        return;

      case PageActionType.SetPagePath:
        getPage(draft, action).page.path = action.path;
        return;

      case PageActionType.SetPagePinterestDescription:
        getPage(draft, action).page.pinterestDescription =
          action.pinterestDescription;
        return;

      case PageActionType.SetPosterImages:
        getPage(draft, action).page.posterImages = action.posterImages;
        return;

      case PageActionType.SetPagePublishAt:
        getPage(draft, action).page.publishAt =
          action.publishAt == null ? null : action.publishAt.toISOString();
        return;

      case PageActionType.SetPageTagIds:
        getPage(draft, action).page.tagIds = action.tagIds;
        return;

      case PageActionType.SetPageTags:
        getPage(draft, action).page.tags = action.tags;
        return;

      case PageActionType.SetPageTitle:
        getPage(draft, action).page.title = action.title;
        return;

      case PageActionType.SetPageUnpublishAt:
        getPage(
          draft,
          action,
        ).page.unpublishAt = action.unpublishAt.toISOString();
        return;

      case PageActionType.UpdatePageError:
        return setPageStatus(draft, action, PageStatus.UpdateError);

      case PageActionType.UpdatePageRequest:
        return setPageStatus(draft, action, PageStatus.UpdateRequested);

      case PageActionType.UpdatePageSuccess:
        return setPageStatus(draft, action, PageStatus.UpdateSuccess);
    }
  });
